import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BlobField from '../components/BlobField';
import Toast from '../components/Toast';
import api, { formatApiErrorDetail } from '../api';
import { useAuth } from '../context/AuthContext';

/**
 * Launch detail — hype page + creator controls.
 * Members can toggle interest, jump to Clan Feed / Chat when the launch is live.
 * Creators see Start/End buttons + a delete option (only if not yet live).
 */
export default function LaunchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [launch, setLaunch] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);

  const reload = () => {
    api.get(`/launches/${id}`).then(({ data }) => setLaunch(data.launch)).catch(() => setLaunch(null));
  };

  useEffect(() => { reload(); }, [id]);

  useEffect(() => {
    if (!launch?.start_at || launch.status !== 'scheduled') return;
    const tick = () => {
      const diff = Math.floor((new Date(launch.start_at).getTime() - Date.now()) / 1000);
      if (diff <= 0) { setCountdown('Starting now…'); reload(); return; }
      const d = Math.floor(diff / 86400);
      const h = Math.floor((diff % 86400) / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setCountdown(d > 0 ? `${d}d ${h}h ${m}m ${s}s` : `${h}h ${m}m ${s}s`);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launch?.start_at, launch?.status]);

  if (!launch) {
    return (
      <>
        <BlobField />
        <div className="loading-shell" data-testid="lchd-loading">loading launch…</div>
      </>
    );
  }

  const isCreator = user?.id === launch.creator_id ||
    (launch.creator && launch.creator.handle === user?.handle);

  const toggleInterest = async () => {
    try {
      const { data } = await api.post(`/launches/${id}/interest`);
      setLaunch((l) => ({ ...l, is_interested: data.is_interested, interested_count: data.interested_count }));
    } catch (ex) {
      setToast({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not update.', tone: 'error' });
    }
  };

  const start = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/launches/${id}/start`);
      setLaunch(data.launch);
      setToast({ msg: '🔴 Launch is LIVE' });
    } catch (ex) {
      setToast({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not start.', tone: 'error' });
    } finally { setBusy(false); }
  };

  const end = async () => {
    if (!confirmEnd) { setConfirmEnd(true); setTimeout(() => setConfirmEnd(false), 3500); return; }
    setConfirmEnd(false);
    try {
      const { data } = await api.post(`/launches/${id}/end`);
      setLaunch(data.launch);
      setToast({ msg: '📴 Launch ended' });
    } catch (ex) {
      setToast({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not end.', tone: 'error' });
    }
  };

  const del = async () => {
    if (!confirmDel) { setConfirmDel(true); setTimeout(() => setConfirmDel(false), 3500); return; }
    setConfirmDel(false);
    try {
      await api.delete(`/launches/${id}`);
      navigate('/launches?filter=mine');
    } catch (ex) {
      setToast({ msg: formatApiErrorDetail(ex.response?.data?.detail) || 'Could not delete.', tone: 'error' });
    }
  };

  const startAt = new Date(launch.start_at);
  const dateLabel = startAt.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const timeLabel = startAt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  return (
    <>
      <BlobField />
      <div className="lchd-wrap" data-testid="launch-detail">
        <div className="lchd-topbar">
          <button className="cf-icon-btn" onClick={() => navigate('/launches')} aria-label="Back" data-testid="lchd-back">
            <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
              <path d="M15 4l-8 8 8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        <div
          className="lchd-hero"
          style={{ background: `linear-gradient(135deg, ${launch.cover_gradient[0]}, ${launch.cover_gradient[1]}, #E040FB)` }}
        >
          <div className="lchd-status">
            {launch.status === 'live' ? <>🔴 LIVE NOW</> :
             launch.status === 'ended' ? 'ENDED' : 'UPCOMING'}
          </div>
          <div className="lchd-hero-avatar" style={{ background: `linear-gradient(135deg, ${launch.creator.gradient[0]}, ${launch.creator.gradient[1]})` }}>
            {launch.creator.initials}
          </div>
        </div>

        <h1 className="lchd-title" data-testid="lchd-title">{launch.title}</h1>
        <div className="lchd-creator">
          by <b>{launch.creator.display_name}</b> · <button className="lchd-link" onClick={() => navigate(`/creator/${launch.clan_handle}`)}>@{launch.clan_handle}</button>
        </div>

        {launch.tags?.length > 0 && (
          <div className="lchd-tags">
            {launch.tags.map((t) => <span key={t} className="lchd-tag">#{t}</span>)}
          </div>
        )}

        <div className="lchd-when">
          <div className="lchd-when-line">
            <span>📅</span>
            <span>{dateLabel} · {timeLabel}</span>
          </div>
          <div className="lchd-when-line">
            <span>🎟️</span>
            <span data-testid="lchd-interested">{launch.interested_count} interested</span>
          </div>
        </div>

        {launch.status === 'scheduled' && countdown && (
          <div className="lchd-countdown" data-testid="lchd-countdown">
            <div className="lchd-count-label">Kicks off in&nbsp;</div>
            <div className="lchd-count-value">{countdown}</div>
          </div>
        )}

        {launch.description && (
          <div className="lchd-desc">
            <div className="lchd-desc-label">About this launch</div>
            <p>{launch.description}</p>
          </div>
        )}

        {/* Actions */}
        <div className="lchd-actions">
          {isCreator ? (
            <>
              {launch.status === 'scheduled' && (
                <button className="cf-primary-btn" style={{ width: '100%' }} onClick={start} disabled={busy} data-testid="lchd-start">
                  {busy ? 'Starting…' : '🔴 Start launch now'}
                </button>
              )}
              {launch.status === 'live' && (
                <>
                  <button className="cf-primary-btn" style={{ width: '100%', marginBottom: 8 }} onClick={() => navigate(`/clan/${launch.clan_handle}/feed?tab=live`, { state: { from: '/launch' } })} data-testid="lchd-open-live">
                    🎥 Open Live in Clan Feed
                  </button>
                  <button className="cf-primary-btn cf-danger-btn" style={{ width: '100%' }} onClick={end} data-testid="lchd-end">
                    {confirmEnd ? 'Tap again to confirm — End launch' : 'End launch'}
                  </button>
                </>
              )}
              {launch.status === 'scheduled' && (
                <button className="cf-secondary-btn" style={{ width: '100%', marginTop: 8 }} onClick={del} data-testid="lchd-delete">
                  {confirmDel ? 'Tap again to confirm — Delete' : 'Delete launch'}
                </button>
              )}
            </>
          ) : (
            <>
              {launch.status === 'live' && (
                <button className="cf-primary-btn" style={{ width: '100%' }} onClick={() => navigate(`/clan/${launch.clan_handle}/feed?tab=live`, { state: { from: '/launch' } })} data-testid="lchd-join-live">
                  🎥 Join Live now
                </button>
              )}
              {launch.status === 'scheduled' && (
                <button
                  className={`cf-primary-btn${launch.is_interested ? ' cf-danger-btn' : ''}`}
                  style={{ width: '100%' }}
                  onClick={toggleInterest}
                  data-testid="lchd-interest-btn"
                >
                  {launch.is_interested ? '🔔 You\'re on the list — Tap to remove' : '🔔 Notify me when it starts'}
                </button>
              )}
              {launch.status === 'ended' && (
                <button className="cf-secondary-btn" style={{ width: '100%' }} onClick={() => navigate(`/creator/${launch.clan_handle}`)} data-testid="lchd-visit-clan">
                  Visit {launch.creator.display_name}'s profile
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
    </>
  );
}
